import { Map, OrderedSet } from 'immutable';
import { IRecordSet, StateRecord, StateRecordSet } from '@mode/shared/contract-common';

export interface AsyncState {
  readonly isLoading: boolean;
  readonly error?: Error;
}

export class ImmutableRecord<T extends StateRecord> {
  data: T;

  constructor(params?: T) {
    this.data = params || ({} as T);
  }

  set<K extends keyof T>(key: K, value: T[K]): this {
    return new (this.constructor as any)(Object.assign({}, this.data, { [key]: value }));
  }

  merge(obj: Partial<T>): this {
    return new (this.constructor as any)(Object.assign({}, this.data, obj));
  }

  get<K extends keyof T>(key: K, defaultValue: T[K] = undefined): T[K] | undefined {
    return this.data[key] ?? defaultValue;
  }

  update<K extends keyof T>(key: K, updater: (val: T[K]) => T[K]) {
    return new (this.constructor as any)(Object.assign({}, this.data, { [key]: updater(this.data[key]) }));
  }
}

/**
 * @deprecated use EMap<T> or similar instead
 */
export class ImmutableRecordSet<T extends StateRecord> implements IRecordSet<T> {
  readonly _keys: IRecordSet<T>['keys'];
  readonly _keyName: keyof T;
  readonly _byKey: IRecordSet<T>['byKey'];

  constructor(keyName: keyof T = 'token', records: T[] = []) {
    this._keyName = keyName;
    this._byKey = Map(records.map<[T[keyof T], T]>((r) => [r[keyName], r]));
    this._keys = OrderedSet<T[keyof T]>(records.map((r) => r[keyName]));
  }

  get keyName(): keyof T {
    return this._keyName;
  }

  get keys(): IRecordSet<T>['keys'] {
    return this._keys;
  }

  get byKey(): IRecordSet<T>['byKey'] {
    return this._byKey;
  }

  get size(): number {
    return this.keys.size;
  }

  clear(): ImmutableRecordSet<T> {
    return new ImmutableRecordSet<T>(this.keyName, []);
  }

  hasRecord(key: T[keyof T]): boolean {
    return this.byKey.has(key);
  }

  getRecord(key: T[keyof T]): T {
    return this.byKey.get(key);
  }

  getRecords(keys?: T[keyof T][]): ReadonlyArray<T> {
    const list = keys || this.keys.toArray();

    return list.map((k) => this.byKey.get(k)).filter((record) => record != null);
  }

  mergeRecord(record: T): ImmutableRecordSet<T> {
    return this.mergeRecords([record]);
  }

  mergeRecords(records: ReadonlyArray<T>): ImmutableRecordSet<T> {
    const keys = this.keys.withMutations((k) => {
      records.forEach((record) => k.add(record[this.keyName]));
    });

    const byKey = this.byKey.withMutations((r) => {
      records.forEach((record) => r.set(record[this.keyName], record));
    });

    return new ImmutableRecordSet<T>(this.keyName, keys.map((k) => byKey.get(k)).toArray());
  }

  replaceRecords(records: T[]): ImmutableRecordSet<T> {
    const newKeys = this.keys.withMutations((keys) => {
      keys.clear();
      records.forEach((record) => keys.add(record[this.keyName]));
    });

    const newByKeys = this.byKey.withMutations((byKey) => {
      byKey.clear();
      records.forEach((record) => byKey.set(record[this.keyName], record));
    });

    return new ImmutableRecordSet<T>(this.keyName, newKeys.map((k) => newByKeys.get(k)).toArray());
  }

  removeRecords(records: ReadonlyArray<T>): ImmutableRecordSet<T> {
    const keys = this.keys.withMutations((k) => {
      for (const record of records) {
        k.remove(record[this.keyName]);
      }
    });

    const byKey = this.byKey.withMutations((r) => {
      for (const record of records) {
        r.remove(record[this.keyName]);
      }
    });

    return new ImmutableRecordSet<T>(this.keyName, keys.map((k) => byKey.get(k)).toArray());
  }

  removeRecordByKey(key: T[keyof T]): ImmutableRecordSet<T> {
    const keys = this.keys.remove(key);
    const byKey = this.byKey.remove(key);

    return new ImmutableRecordSet<T>(this.keyName, keys.map((k) => byKey.get(k)).toArray());
  }

  removeRecordsByKey(keysToRemove: T[keyof T][]): ImmutableRecordSet<T> {
    if (!keysToRemove) {
      return this;
    }
    const keys = this.keys.filterNot((key) => keysToRemove.indexOf(key) >= 0);
    const byKey = this.byKey.filterNot((entry) => keysToRemove.indexOf(entry[this.keyName]) >= 0);

    return new ImmutableRecordSet<T>(this.keyName, keys.map((k) => byKey.get(k)).toArray());
  }
}
